import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const rodeoOpsKeys = {
  all: ['rodeo-ops'] as const,
  list: (filters?: RodeosListFilters) =>
    filters
      ? ([...rodeoOpsKeys.all, 'list', filters] as const)
      : ([...rodeoOpsKeys.all, 'list'] as const),
  detail: (rodeoId: string) =>
    [...rodeoOpsKeys.all, 'detail', rodeoId] as const,
};

// --- Types ---
interface Rodeo {
  id: string;
  name: string;
  rodeo_or_jackpot: string;
  is_published: boolean;
  events?: any[];
  /** Server-aggregated count of active (non-cancelled) event_registrations
   *  summed across every event in this rodeo. Surfaced on rodeo cards so
   *  visitors can see participation at a glance without paying for an
   *  N+1 fan-out. Always present on /jackpot-ops/list responses (0 when
   *  the rodeo has no events / no active registrations). */
  total_event_entries?: number;
  [key: string]: unknown;
}

interface RodeosListFilters {
  page?: number;
  page_size?: number;
  organization_id?: string;
  is_published?: boolean;
  /** When false, the query is disabled (useful to wait for org context). */
  enabled?: boolean;
}

// --- Query Hooks ---
export function useRodeosList(filters?: RodeosListFilters) {
  const qs = new URLSearchParams();
  qs.set('type', 'rodeo');
  if (filters?.page) qs.set('page', String(filters.page));
  if (filters?.page_size) qs.set('page_size', String(filters.page_size));
  if (filters?.organization_id)
    qs.set('organization_id', filters.organization_id);
  if (filters?.is_published !== undefined)
    qs.set('is_published', String(filters.is_published));
  const query = `?${qs}`;
  return useQuery({
    queryKey: rodeoOpsKeys.list(filters),
    queryFn: () =>
      httpClient.get<{ data: Rodeo[]; total: number }>(
        `/jackpot-ops/list${query}`,
      ),
    enabled: filters?.enabled !== false,
  });
}

export function useRodeoDetail(rodeoId: string) {
  return useQuery({
    queryKey: rodeoOpsKeys.detail(rodeoId),
    queryFn: () => httpClient.get<Rodeo>(`/jackpot-ops/${rodeoId}`),
    enabled: !!rodeoId,
  });
}

/**
 * Mark a rodeo cancelled — or reinstate it.
 *
 * `rodeos.cancelled_at` is the single flag that tells the rest of the system
 * "this rodeo is not happening", and it has real financial consequences:
 * entries stop reporting an outstanding balance (so they can never roll into
 * past-due fees that block the contestant from the org's future rodeos),
 * already-drawn entries become cancellable, and cancelling one refunds the
 * transaction fee by default.
 *
 * Distinct from unpublishing, which only hides the rodeo from browsing and is
 * the normal state for any rodeo still being built.
 *
 * Deliberately does NOT cancel the entries — that is a separate, reviewable
 * bulk step (scripts/bulk-cancel-rodeo-entries.ts), because it moves money.
 */
export function useSetRodeoCancellation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      rodeoId: string;
      cancelled: boolean;
      reason?: string | null;
    }) =>
      httpClient.post<{ success: boolean; rodeo: Rodeo }>(
        `/registration-payment-ops/rodeos/${vars.rodeoId}/cancellation`,
        {
          cancelled: vars.cancelled,
          ...(vars.reason != null ? { reason: vars.reason } : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rodeoOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['rodeos'] });
      // Balances and entry lists both change shape once a rodeo is cancelled.
      qc.invalidateQueries({ queryKey: ['admin-entries'] });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
    },
  });
}
